import { useState, useEffect, useRef, useCallback } from 'react';
import { RoomEvent } from 'matrix-js-sdk/lib/models/room';
import logger from '../utils/logger';
import { SUPPORTED_MESSAGE_TYPES } from '../constants';

/**
 * Maximum number of events to keep in the window
 * This helps manage memory usage
 */
const MAX_WINDOW_SIZE = 500;

/**
 * Number of events to paginate when loading more
 */
const PAGINATION_SIZE = 50;

/**
 * Minimum number of events to keep when unpaginating
 * to avoid aggressive pruning
 */
const MIN_WINDOW_SIZE = 100;

/**
 * Target number of events to ensure viewport is filled
 * The hook will recursively paginate until this threshold is met
 */
const VIEWPORT_FILL_TARGET = 30;

/**
 * Maximum number of recursive pagination attempts
 * Prevents infinite loops in edge cases
 */
const MAX_FILL_ATTEMPTS = 10;

/**
 * Hook for managing a timeline window with pagination and memory management
 *
 * @param {Object} room - The Matrix room object
 * @param {string} roomId - The room ID for reference
 * @returns {Object} Timeline window state and controls
 */
export const useTimelineWindow = (room, roomId) => {
  const [events, setEvents] = useState([]);
  const [isLoadingOlder, setIsLoadingOlder] = useState(false);
  const [isLoadingNewer, setIsLoadingNewer] = useState(false);
  const [canPaginateBackward, setCanPaginateBackward] = useState(true);
  const [canPaginateForward, setCanPaginateForward] = useState(false);
  const [windowPosition, setWindowPosition] = useState('live'); // 'live', 'historical', or 'middle'
  const [isFillingViewport, setIsFillingViewport] = useState(false);

  // Keep track of the timeline we're working with
  const timelineRef = useRef(null);
  const eventTimelineSetRef = useRef(null);

  /**
   * Check if we can paginate backward based on Matrix SDK pagination tokens
   * This is the standard and recommended approach with a fallback
   */
  const canPaginateBackwardFromToken = useCallback((timeline) => {
    if (!timeline) {
      return false;
    }
    const backwardToken = timeline.getPaginationToken('b');
    const hasToken = backwardToken !== null && backwardToken !== undefined;

    if (!hasToken) {
      const events = timeline.getEvents();
      if (events.length > 0) {
        const oldestEvent = events[0];
        const isRoomCreate = oldestEvent.getType() === 'm.room.create';
        return !isRoomCreate;
      }
    }
    return hasToken;
  }, []);

  /**
   * Recursively fill the viewport by paginating backward until we have enough content
   */
  // Helper: count visible (displayable) messages in a list of events
  const countVisibleMessages = (events) => {
    let count = 0;
    for (const event of events) {
      // Room creation event is always visible
      if (event.getType() === 'm.room.create') {
        count++;
        continue;
      }
      // Decrypted message
      if (event.getClearContent && event.getClearContent()) {
        const clearContent = event.getClearContent();
        if (SUPPORTED_MESSAGE_TYPES.includes(clearContent.msgtype)) {
          count++;
        }
        continue;
      }
      // Unencrypted message
      if (event.getType() === 'm.room.message') {
        const content = event.getContent();
        if (SUPPORTED_MESSAGE_TYPES.includes(content.msgtype)) {
          count++;
        }
        continue;
      }
      // Show encrypted messages that failed to decrypt
      if (event.getType() === 'm.room.encrypted') {
        count++;
        continue;
      }
    }
    return count;
  };

  const fillViewport = useCallback(
    async (currentAttempts = 0) => {
      if (!room || !timelineRef.current || currentAttempts >= MAX_FILL_ATTEMPTS) {
        logger.debug(`[TimelineWindow] fillViewport: Stopping (room? ${!!room}, timeline? ${!!timelineRef.current}, attempts=${currentAttempts})`);
        setIsFillingViewport(false);
        return;
      }

      const timeline = timelineRef.current;
      const currentEvents = timeline.getEvents();
      const visibleCount = countVisibleMessages(currentEvents);

      // Check if we have enough visible messages or can't paginate anymore
      if (visibleCount >= VIEWPORT_FILL_TARGET || !canPaginateBackward) {
        setIsFillingViewport(false);
        return;
      }

      try {
        setIsFillingViewport(true);

        // Track previous event count for infinite loop detection
        const prevCount = timeline.getEvents().length;

        // Paginate backward to get more content
        const paginateResult = await room.client.paginateEventTimeline(timeline, {
          backwards: true,
          limit: PAGINATION_SIZE,
        });

        const updatedEvents = timeline.getEvents();
        const newCount = updatedEvents.length;
        const newVisibleCount = countVisibleMessages(updatedEvents);

        if (paginateResult) {
          if (newCount === prevCount) {
            logger.warn('[TimelineWindow] fillViewport: Paginate returned no new events, breaking to avoid infinite loop.');
            setIsFillingViewport(false);
            return;
          }
          // ...existing code...
          setEvents([...updatedEvents]);

          // Check if we can still paginate backward using Matrix SDK tokens
          const canStillPaginate = canPaginateBackwardFromToken(timeline);

          setCanPaginateBackward(canStillPaginate);

          // Recursively continue filling if we still need more visible messages
          if (newVisibleCount < VIEWPORT_FILL_TARGET && canStillPaginate) {
            // Use a small delay to prevent overwhelming the server
            setTimeout(() => {
              fillViewport(currentAttempts + 1);
            }, 100);
          } else {
            setIsFillingViewport(false);
          }
        } else {
          setCanPaginateBackward(false); // No more events available
          setIsFillingViewport(false);
        }
      } catch (error) {
        logger.error('useTimelineWindow: Error during viewport fill:', error);
        setIsFillingViewport(false);
      }
    },
  [room, canPaginateBackward, canPaginateBackwardFromToken],
  );

  /**
   * Initialize the timeline window
   */
  const initializeTimeline = useCallback(() => {
    if (!room) {
      setEvents([]);
      setCanPaginateBackward(true);
      setCanPaginateForward(false);
      setWindowPosition('live');
      setIsFillingViewport(false);
      return;
    }

    // Get the live timeline
    const liveTimeline = room.getLiveTimeline();
    const eventTimelineSet = room.getTimelineSets()[0]; // Get the main timeline set

    timelineRef.current = liveTimeline;
    eventTimelineSetRef.current = eventTimelineSet;

    // Start with the live timeline events
    const initialEvents = liveTimeline.getEvents();
    setEvents([...initialEvents]);

    // Check if we can paginate using Matrix SDK pagination tokens
    const canInitiallyPaginateBackward = canPaginateBackwardFromToken(liveTimeline);

    setCanPaginateBackward(canInitiallyPaginateBackward);
    setCanPaginateForward(false); // Can't paginate forward from live
    setWindowPosition('live');

    // Start filling the viewport if we don't have enough content
    if (initialEvents.length < VIEWPORT_FILL_TARGET) {
      fillViewport(0);
    }
  }, [room, roomId, fillViewport, canPaginateBackwardFromToken]);

  /**
   * Paginate backward (load older events)
   */
  const paginateBackward = useCallback(async () => {
    if (!room || !timelineRef.current || isLoadingOlder || !canPaginateBackward) {
      return;
    }

    setIsLoadingOlder(true);

    try {
      // Use the timeline's paginate method to load older events
      const paginateResult = await room.client.paginateEventTimeline(timelineRef.current, {
        backwards: true,
        limit: PAGINATION_SIZE,
      });

      if (paginateResult) {
        // Get the updated events from the timeline
        const updatedEvents = timelineRef.current.getEvents();
        setEvents([...updatedEvents]);
        logger.debug(
          `[TimelineWindow] paginateBackward: Paginated backward, now have ${updatedEvents.length} events.`,
        );
        // Check if we can still paginate backward using Matrix SDK tokens
        const canStillPaginate = canPaginateBackwardFromToken(timelineRef.current);
        setCanPaginateBackward(canStillPaginate);

        // If we were at live position and loaded older events, we're now in historical view
        if (windowPosition === 'live') {
          setWindowPosition('historical');
          setCanPaginateForward(true); // Now we can paginate forward to get back to live
        }

        // Manage memory by unpaginating if we have too many events
        await manageWindowSize();
      } else {
        setCanPaginateBackward(false); // No more events available
      }
    } catch (error) {
      logger.error('useTimelineWindow: Error paginating backward:', error);
    } finally {
      setIsLoadingOlder(false);
    }
  }, [
    room,
    roomId,
    isLoadingOlder,
    canPaginateBackward,
    windowPosition,
    canPaginateBackwardFromToken,
  ]);

  /**
   * Paginate forward (load newer events)
   */
  const paginateForward = useCallback(async () => {
    if (!room || !timelineRef.current || isLoadingNewer || !canPaginateForward) {
      return;
    }

    setIsLoadingNewer(true);

    try {
      // Use the timeline's paginate method to load newer events
      const paginateResult = await room.client.paginateEventTimeline(timelineRef.current, {
        backwards: false,
        limit: PAGINATION_SIZE,
      });

      if (paginateResult) {
        // Get the updated events from the timeline
        const updatedEvents = timelineRef.current.getEvents();
        setEvents([...updatedEvents]);

        // Check if we can still paginate forward
        const canStillPaginate = timelineRef.current.getPaginationToken('f') !== null;
        setCanPaginateForward(canStillPaginate);

        // If we can't paginate forward anymore, we're back at live
        if (!canStillPaginate) {
          setWindowPosition('live');
        } else {
          setWindowPosition('middle');
        }

        // Manage memory by unpaginating if we have too many events
        await manageWindowSize();
      }
    } catch (error) {
      logger.error('useTimelineWindow: Error paginating forward:', error);
    } finally {
      setIsLoadingNewer(false);
    }
  }, [room, roomId, isLoadingNewer, canPaginateForward]);

  /**
   * Jump to live timeline (most recent events)
   */
  const jumpToLive = useCallback(() => {
    if (!room) return;

    // Get the live timeline and reset our window
    const liveTimeline = room.getLiveTimeline();
    timelineRef.current = liveTimeline;

    const liveEvents = liveTimeline.getEvents();
    setEvents([...liveEvents]);
    setWindowPosition('live');
    setCanPaginateBackward(true);
    setCanPaginateForward(false);
  }, [room]);

  /**
   * Manage window size by unpaginating old events to prevent memory issues
   */
  const manageWindowSize = useCallback(async () => {
    if (!timelineRef.current || events.length <= MAX_WINDOW_SIZE) {
      return;
    }

    try {
      // If we're viewing historical content and have too many events,
      // unpaginate from the end (newer events) to keep the older ones visible
      if (windowPosition === 'historical') {
        // Keep the older events, remove newer ones
        const eventsToKeep = MIN_WINDOW_SIZE;
        const timeline = timelineRef.current;
        const allEvents = timeline.getEvents();

        if (allEvents.length > eventsToKeep) {
          // This is a simplified approach - in a real implementation,
          // you might want to use the Matrix SDK's unpaginate methods
          // For now, we'll just work with a subset
          const keptEvents = allEvents.slice(0, eventsToKeep);
          setEvents([...keptEvents]);
          setCanPaginateForward(true); // We removed newer events, so we can paginate forward
        }
      } else {
        // If we're viewing live or middle content, keep the newer events
        const eventsToKeep = MIN_WINDOW_SIZE;
        const timeline = timelineRef.current;
        const allEvents = timeline.getEvents();

        if (allEvents.length > eventsToKeep) {
          const keptEvents = allEvents.slice(-eventsToKeep);
          setEvents([...keptEvents]);
          setCanPaginateBackward(true); // We removed older events, so we can paginate backward
        }
      }
    } catch (error) {
      logger.error('useTimelineWindow: Error managing window size:', error);
    }
  }, [events.length, windowPosition]);

  /**
   * Handle live timeline updates
   */
  const handleTimelineUpdate = useCallback(
    (event, eventRoom, toStartOfTimeline, removed, data) => {
      if (eventRoom?.roomId !== roomId) return;

      // Only process live events when we're viewing the live timeline
      if (data?.liveEvent && windowPosition === 'live') {
        const updatedEvents = eventRoom.getLiveTimeline().getEvents();
        setEvents([...updatedEvents]);

        // Manage memory for live updates too
        if (updatedEvents.length > MAX_WINDOW_SIZE) {
          manageWindowSize();
        }
      }
    },
  [roomId, windowPosition, manageWindowSize],
  );

  /**
   * Handle timeline reset
   */
  const handleTimelineReset = useCallback(
    (eventRoom) => {
      if (eventRoom?.roomId !== roomId) return;
      initializeTimeline();
    },
  [roomId, initializeTimeline],
  );

  // Initialize timeline when room changes
  useEffect(() => {
    initializeTimeline();
  }, [initializeTimeline]);

  // Set up timeline event listeners
  useEffect(() => {
    if (!room?.client) return;

    const client = room.client;

    client.on(RoomEvent.Timeline, handleTimelineUpdate);
    client.on(RoomEvent.TimelineReset, handleTimelineReset);

    return () => {
      client.removeListener(RoomEvent.Timeline, handleTimelineUpdate);
      client.removeListener(RoomEvent.TimelineReset, handleTimelineReset);
    };
  }, [room, handleTimelineUpdate, handleTimelineReset]);

  return {
    events,
    isLoadingOlder,
    isLoadingNewer,
    canPaginateBackward,
    canPaginateForward,
    windowPosition,
    isFillingViewport,
    paginateBackward,
    paginateForward,
    jumpToLive,
    manageWindowSize,
    initializeTimeline,
  };
};

export default useTimelineWindow;
